import dayjs from "dayjs";
import * as XLSX from "xlsx";

import type { LibroMayorLine, PublishedMoveLine } from "~/types/contabilidad";

interface LibroMayorDetailExportData {
  summaryLines: LibroMayorLine[];
  detailLines: PublishedMoveLine[];
  period: string;
  companyName: string;
  accountName?: string;
}

/**
 * Exports the Libro Mayor movement details to Excel with saldo inicial/final
 */
export const exportLibroMayorDetailToExcel = ({
  summaryLines,
  detailLines,
  period,
  companyName,
  accountName,
}: LibroMayorDetailExportData): void => {
  const workbook = XLSX.utils.book_new();

  // Create a map of account_id to summary data for quick lookup
  const summaryMap = new Map<string, LibroMayorLine>();
  summaryLines.forEach((line) => {
    summaryMap.set(line.account_id, line);
  });

  // Group detail lines by account
  const groupedByAccount = new Map<string, PublishedMoveLine[]>();
  detailLines.forEach((line) => {
    const existing = groupedByAccount.get(line.account_id) || [];
    existing.push(line);
    groupedByAccount.set(line.account_id, existing);
  });

  // Sort accounts by code
  const sortedAccountIds = Array.from(groupedByAccount.keys()).sort((a, b) => {
    const accountA = summaryMap.get(a);
    const accountB = summaryMap.get(b);
    return (accountA?.account_code || "").localeCompare(
      accountB?.account_code || ""
    );
  });

  // Build sheet data
  const sheetData: (string | number)[][] = [];

  // Table header
  const tableHeader = [
    "Código Cuenta",
    "Cuenta",
    "Fecha",
    "N° Asiento",
    "Diario",
    "Descripción",
    "Débito",
    "Crédito",
    "Saldo",
  ];
  sheetData.push(tableHeader);

  let grandTotalDebit = 0;
  let grandTotalCredit = 0;

  // For each account, add saldo inicial, movements, and saldo final
  sortedAccountIds.forEach((accountId) => {
    const summary = summaryMap.get(accountId);
    const movements = groupedByAccount.get(accountId) || [];

    if (!summary) return;

    // Sort movements by date
    movements.sort(
      (a, b) =>
        new Date(a.move_date).getTime() - new Date(b.move_date).getTime()
    );

    // Saldo Inicial row
    sheetData.push([
      summary.account_code,
      summary.account_name,
      "",
      "",
      "",
      "SALDO INICIAL",
      "",
      "",
      Number(summary.saldo_inicial),
    ]);

    // Movement rows with running balance
    let runningBalance = Number(summary.saldo_inicial);
    movements.forEach((line) => {
      const debit = Number(line.debit);
      const credit = Number(line.credit);
      runningBalance = runningBalance + debit - credit;
      grandTotalDebit += debit;
      grandTotalCredit += credit;

      sheetData.push([
        summary.account_code,
        summary.account_name,
        dayjs(line.move_date).format("DD/MM/YYYY"),
        line.move_number,
        line.journal_name,
        line.description || line.move_ref || "",
        debit,
        credit,
        runningBalance,
      ]);
    });

    // Saldo Final row
    sheetData.push([
      summary.account_code,
      summary.account_name,
      "",
      "",
      "",
      "SALDO FINAL",
      "",
      "",
      Number(summary.saldo_final),
    ]);

    // Empty row between accounts
    sheetData.push([]);
  });

  // Grand totals row
  sheetData.push([
    "",
    "",
    "",
    "",
    "",
    "TOTALES",
    grandTotalDebit,
    grandTotalCredit,
    "",
  ]);

  // Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);

  // Set column widths
  worksheet["!cols"] = [
    { wch: 12 }, // Código Cuenta
    { wch: 35 }, // Cuenta
    { wch: 12 }, // Fecha
    { wch: 14 }, // N° Asiento
    { wch: 15 }, // Diario
    { wch: 40 }, // Descripción
    { wch: 18 }, // Débito
    { wch: 18 }, // Crédito
    { wch: 18 }, // Saldo
  ];

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, "Detalle Movimientos");

  // Generate filename
  const filename = `libro_mayor_detalle_${dayjs().format("YYYY-MM-DD")}.xlsx`;

  // Download file
  XLSX.writeFile(workbook, filename);
};
